import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  getDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase";
import LoadingComLayout from "../components/LoadingComLayout";
import {
  LuMegaphone, LuScissors, LuCheck, LuX,
  LuClock, LuUsers, LuArmchair, LuPlus,
  LuLock, LuLockOpen, LuCalendarClock,
  LuTriangleAlert, LuUndo2, LuUser,
  LuFilter, LuHourglass, LuSettings2, LuBellRing, LuTv
} from "react-icons/lu";

// ======= ESTILOS =======

const Main = styled.div`
  flex: 1;
  padding: 2rem 4rem;
  background: #f3f7ff;
  min-height: 100%;
  display: flex; flex-direction: column;
  @media (max-width: 768px){ padding: 1rem; }
`;

const PageHeader = styled.div`
`;

// --- TOAST NOTIFICATION (ATUALIZADO: MAIOR E COM BOTÃO FECHAR) ---
const slideInToast = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 30px;
  right: 30px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 450px;
  width: 100%;

  /* --- AJUSTE MOBILE --- */
  @media (max-width: 768px) {
    top: 20px;             /* Fica bem no topo */
    left: 50%;             /* Ponto de ancoragem no meio */
    right: auto;           /* Remove alinhamento à direita */
    transform: translateX(-50%); /* Centraliza perfeitamente */
    width: 95%;            /* Ocupa quase toda a largura da tela */
    max-width: 95%;
  }
`;

const Toast = styled.div`
  background: white;
  border-left: 8px solid #f59e0b;
  padding: 20px 25px;
  border-radius: 12px; /* Borda um pouco mais arredondada */
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  display: flex;
  align-items: flex-start;
  gap: 15px;
  position: relative;
  animation: ${slideInToast} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  h4 { 
    margin: 0 0 6px 0; 
    font-size: 1.1rem; 
    color: #1e293b; 
    font-weight: 700;
  }
  p { 
    margin: 0; 
    font-size: 0.95rem; 
    color: #64748b; 
    line-height: 1.4;
  }
  
  .icon-box {
    background: #fef3c7;
    color: #d97706;
    width: 45px; height: 45px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    font-size: 1.5rem;
  }

  /* --- AJUSTE MOBILE PARA DAR MAIS DESTAQUE --- */
  @media (max-width: 768px) {
    border-left-width: 12px; /* Borda lateral mais grossa */
    box-shadow: 0 20px 60px rgba(0,0,0,0.5); /* Sombra bem forte para destacar do fundo */
    border: 1px solid rgba(0,0,0,0.05); /* Leve contorno */
    
    /* Ajuste para o texto não quebrar feio em telas muito pequenas */
    padding: 15px; 
    h4 { font-size: 1rem; }
    p { font-size: 0.9rem; }
  }
`;

const ToastCloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #ef4444;
  }
`;

// Banner de Próximo Agendamento
const NextAppointmentCard = styled.div`
  background: linear-gradient(135deg, #3a79ed 0%, #2a5ec2 100%);
  border-radius: 1rem;
  padding: 1rem 1.5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(58, 121, 237, 0.2);
  margin-bottom: 1rem;
  animation: fadeIn 0.5s ease-out;
`;

const NextAppInfo = styled.div`
  display: flex; align-items: center; gap: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  span { font-weight: 400; font-size: 0.9rem; opacity: 0.9; }
`;

// Faixa Vermelha de Fila Fechada
const ClosedBanner = styled.div`
  background-color: #fee2e2;
  color: #ef4444;
  padding: 1rem;
  text-align: center;
  font-weight: 700;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px dashed #ef4444;
`;

const GridArea = styled.div`
  display: grid; gap: 1.5rem; grid-template-columns: 1.4fr 0.6fr; 
  align-items: start;
  @media (max-width: 1100px){ grid-template-columns: 1fr; }
`;

const Card = styled.section`
  background: #fff; 
  border: 1px solid #e6ecf5; 
  border-radius: 1rem;
  box-shadow: 0 6px 20px rgba(58,121,237,.06);
  overflow: hidden;
  display: flex; flex-direction: column;
  height: 100%;
`;

const Toolbar = styled.div`
  background: #fff;
  border-bottom: 1px solid #eef2fb;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ToolbarTop = styled.div`
  display: flex; justify-content: space-between; align-items: center;
`;

const ToolbarTitle = styled.div`
  display: flex; align-items: center; gap: 0.75rem;
  font-size: 1.1rem; font-weight: 700; color: #1f1a40;
  
  .count-badge {
    background: #e0f2fe; color: #0284c7;
    font-size: 0.8rem; padding: 2px 8px; border-radius: 12px;
  }
`;

const ToolbarControls = styled.div`
  display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
  padding-top: 0.5rem;
  border-top: 1px dashed #eef2fb;

  @media (max-width: 600px) {
    flex-direction: column; align-items: stretch;
  }
`;

const ControlGroup = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding-right: 10px;
  margin-right: 10px;
  border-right: 1px solid #eef2fb;

  &:last-child { border-right: none; margin-right: 0; }
  @media (max-width: 600px) { border-right: none; margin-right: 0; padding-right: 0; border-bottom: 1px solid #eef2fb; padding-bottom: 10px; }
`;

const CardBody = styled.div`
  padding: 1.5rem; 
  display: flex; flex-direction: column; gap: 1rem;
  flex: 1;
  min-height: 400px;
`;

const TicketCard = styled.div`
  background: ${(props) => props.bg || "#fff"};
  border: 1px solid ${(props) => props.borderColor || "#e5e7eb"};
  border-left: 4px solid ${(props) => props.borderColor || "transparent"};
  border-radius: 0.5rem;
  padding: 1rem;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);

  &:hover {
    transform: translateX(2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  }
`;

const TicketHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;
`;
const ClientName = styled.h4` font-size: 1rem; font-weight: 700; color: #1f1a40; margin: 0; `;
const TicketMeta = styled.div` font-size: 0.85rem; color: #6b7280; margin-top: 2px; `;

const StatusTag = styled.span`
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  padding: 2px 6px; border-radius: 4px;
  background: ${props => props.bg}; color: ${props => props.color};
`;

const ActionRow = styled.div` display: flex; gap: 0.5rem; margin-top: 1rem; `;

const ActionButton = styled.button`
  flex: 1; padding: 0.5rem; font-weight: 600; border: none; border-radius: 0.375rem;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  font-size: 0.85rem; transition: 0.2s;
  background-color: ${(props) => props.bg}; color: ${(props) => props.color};
  &:hover { filter: brightness(0.95); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ToolbarSelect = styled.select`
  padding: 0.4rem 2rem 0.4rem 0.8rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: white;
  font-size: 0.85rem; color: #374151;
  cursor: pointer;
  height: 32px;
  &:focus { outline: 2px solid #3a79ed; border-color: #3a79ed; }
`;

const ToolbarLabel = styled.span`
  font-size: 0.75rem; font-weight: 600; color: #6b7280; margin-right: 5px; display: flex; align-items: center; gap: 4px;
`;

const EmptyState = styled.div` text-align: center; padding: 3rem 1rem; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 10px; svg { font-size: 2.5rem; color: #e5e7eb; } `;
const ModalOverlay = styled.div` position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px; `;
const ModalContent = styled.div` background: white; padding: 2rem; border-radius: 1rem; width: 100%; max-width: 450px; box-shadow: 0 10px 25px rgba(0,0,0,0.15); animation: fadeIn 0.2s ease-out; `;
const ModalActions = styled.div` display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px;`;
const InputGroup = styled.div` margin-bottom: 1rem; label { display: block; margin-bottom: 0.5rem; font-weight: 600; color: #1f1a40; } input, select { width: 100%; padding: 0.75rem; border-radius: 0.5rem; border: 1px solid #d1d5db; font-size: 1rem; } `;

// ======= COMPONENTES AUXILIARES =======

// Timer Animado para o Ticket
const pulseRed = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

const TimerWrapper = styled.div`
  margin-top: 10px;
  padding: 8px;
  background: ${props => props.expired ? '#fee2e2' : '#f0f9ff'};
  border: 1px solid ${props => props.expired ? '#ef4444' : '#bae6fd'};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${props => props.expired ? css`${pulseRed} 1s infinite` : 'none'};
`;

const TimerText = styled.div`
  font-weight: 700;
  color: ${props => props.expired ? '#dc2626' : '#0284c7'};
  font-size: 0.9rem;
  display: flex; justify-content: space-between; align-items: center;
`;

const TimerProgress = styled.div`
  width: 100%; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden;
`;

const TimerBar = styled.div`
  height: 100%; 
  background: ${props => props.expired ? '#ef4444' : '#0ea5e9'};
  width: ${props => props.width}%;
  transition: width 1s linear;
`;

// Componente do Timer Lógico
const TicketTimer = ({ startTime, toleranceMinutes }) => {
  const calculateState = useCallback(() => {
    if (!startTime) return { text: "Calculando...", progress: 100, expired: false };

    const totalMs = toleranceMinutes * 60 * 1000;
    const startMs = startTime.toDate().getTime();
    const now = new Date().getTime();
    const elapsed = now - startMs;
    const remaining = totalMs - elapsed;

    if (remaining <= 0) {
      return { text: "TEMPO ESGOTADO", progress: 0, expired: true };
    } else {
      const m = Math.floor(remaining / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      const text = `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      const progress = (remaining / totalMs) * 100;
      return { text, progress, expired: false };
    }
  }, [startTime, toleranceMinutes]);

  const [timerState, setTimerState] = useState(calculateState());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimerState(calculateState());
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateState]);

  return (
    <TimerWrapper expired={timerState.expired}>
      <TimerText expired={timerState.expired}>
        <span>{timerState.expired ? "Cliente atrasado" : "Aguardando..."}</span>
        <span>{timerState.text}</span>
      </TimerText>
      <TimerProgress>
        <TimerBar width={timerState.progress} expired={timerState.expired} />
      </TimerProgress>
    </TimerWrapper>
  );
};

// ======= HELPERS =======
const getTodayString = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const parseTime = (timeStr) => { const [h, m] = timeStr.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0); return d; };

export default function FilaAdmin() {
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [loadingId, setLoadingId] = useState(true);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [isOwner, setIsOwner] = useState(false);

  const [fila, setFila] = useState([]);
  const [emAtendimento, setEmAtendimento] = useState([]);
  const [agendamentosHoje, setAgendamentosHoje] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [barbeirosList, setBarbeirosList] = useState([]);
  const [filaAberta, setFilaAberta] = useState(false);
  const [tolerancia, setTolerancia] = useState(5);

  const [filterBarbeiro, setFilterBarbeiro] = useState("");
  const [modalManualOpen, setModalManualOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", type: "info", onConfirm: null });

  const [manualNome, setManualNome] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualServico, setManualServico] = useState("");
  const [manualBarbeiro, setManualBarbeiro] = useState("");

  // Estado para Notificações (Toasts)
  const [notifications, setNotifications] = useState([]);

  const db = getFirestore(app);
  const auth = getAuth(app);

  const abrirModoTv = () => {
    if (!barbeariaId) return;
    // Abre em nova aba ('_blank')
    const url = `/tv/${barbeariaId}`;
    window.open(url, '_blank');
  };

  // Função para adicionar notificação
  const addNotification = (title, message) => {
    const id = Date.now();
    // Duração aumentada para 30 segundos, ou fechar no X
    setNotifications(prev => [...prev, { id, title, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 30000);
  };

  // Função para remover notificação manualmente
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        const qOwner = query(collection(db, "barbearia"), where("criadorUid", "==", user.uid));
        const snapOwner = await getDocs(qOwner);
        if (!snapOwner.empty) {
          setBarbeariaId(snapOwner.docs[0].id); setIsOwner(true); setLoadingId(false); return;
        }
        const barbeariasSnap = await getDocs(collection(db, "barbearia"));
        for (const docBarbearia of barbeariasSnap.docs) {
          const barbeiroRef = doc(db, `barbearia/${docBarbearia.id}/barbeiros/${user.uid}`);
          const barbeiroSnap = await getDoc(barbeiroRef);
          if (barbeiroSnap.exists()) {
            setBarbeariaId(docBarbearia.id); setUserName(barbeiroSnap.data().nome || user.displayName);
            setIsOwner(false); setLoadingId(false); return;
          }
        }
        setLoadingId(false);
      } else { setLoadingId(false); }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!barbeariaId) return;
    const todayStr = getTodayString();

    const unsubBarbearia = onSnapshot(doc(db, "barbearia", barbeariaId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setFilaAberta(data.filaAberta === true);
        if (data.toleranciaFila) setTolerancia(Number(data.toleranciaFila));
      }
    });
    getDocs(collection(db, "barbearia", barbeariaId, "servico")).then(snap => setServicos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    getDocs(collection(db, "barbearia", barbeariaId, "barbeiros")).then(snap => setBarbeirosList(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    let unsubAgendamentos = () => { };
    if (userId) {
      const baseQuery = [
        where("data", "==", todayStr),
        where("status", "==", "confirmado")
      ];
      let qAgendamentos;
      if (!isOwner) {
        qAgendamentos = query(
          collection(db, "barbearia", barbeariaId, "agendamentos"),
          ...baseQuery,
          where("barbeiroId", "==", userId)
        );
      } else {
        qAgendamentos = query(
          collection(db, "barbearia", barbeariaId, "agendamentos"),
          ...baseQuery
        );
      }
      unsubAgendamentos = onSnapshot(qAgendamentos, (snap) => {
        const ag = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        ag.sort((a, b) => parseTime(a.horaInicio) - parseTime(b.horaInicio));
        setAgendamentosHoje(ag);
      });
    }

    const qFila = query(collection(db, "barbearia", barbeariaId, "fila"), where("status", "in", ["aguardando", "chamado", "em_atendimento"]));
    const unsubFila = onSnapshot(qFila, (snapshot) => {
      const todos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      snapshot.docChanges().forEach((change) => {
        if (change.type === "modified") {
          const data = change.doc.data();

          // Verifica se tem a flag de pulou e se está aguardando
          if (data.pulouVezEm && data.status === "aguardando") {
            let pulouTimestamp = 0;

            // 1. Proteção contra erro de leitura do Timestamp (serverTimestamp pode vir null brevemente)
            if (data.pulouVezEm?.toDate) {
              pulouTimestamp = data.pulouVezEm.toDate().getTime();
            } else if (data.pulouVezEm instanceof Date) {
              pulouTimestamp = data.pulouVezEm.getTime();
            } else {
              // Fallback para string ou número
              pulouTimestamp = new Date(data.pulouVezEm).getTime();
            }

            const agora = Date.now();
            const diferenca = agora - pulouTimestamp;

            // 2. AUMENTO DA TOLERÂNCIA e Proteção de Relógio:
            // - Aumentei para 60000ms (1 minuto) para garantir que notificações cheguem mesmo com lag.
            // - Adicionei "diferenca > -10000" para casos onde o relógio do PC local está errado (no futuro).
            if (diferenca < 60000 && diferenca > -10000) {
              addNotification(
                "Cliente Pulou a Vez!",
                `O cliente ${data.clienteNome} optou por pular a vez e voltou para o fim da fila.`
              );
            }
          }
        }
      });

      todos.sort((a, b) => (a.dataEntrada?.seconds || 0) - (b.dataEntrada?.seconds || 0));
      setFila(todos.filter(t => ["aguardando", "chamado"].includes(t.status)));
      const emAtendimentoVisiveis = isOwner ? todos.filter(t => t.status === "em_atendimento") : todos.filter(t => t.status === "em_atendimento" && t.barbeiroId === userId);
      setEmAtendimento(emAtendimentoVisiveis);
    });
    return () => { unsubBarbearia(); unsubFila(); unsubAgendamentos(); };
  }, [barbeariaId, userId, isOwner, db]);

  // ======= FUNÇÕES =======
  const handleToleranceChange = (newVal) => {
    const minutes = Number(newVal);
    openConfirm("Alterar Tolerância", `Alterar tolerância para ${minutes} minutos?`, async () => {
      await updateDoc(doc(db, "barbearia", barbeariaId), { toleranciaFila: minutes });
    }, "info");
  };

  const toggleFilaStatus = async () => {
    if (!barbeariaId) return;
    await updateDoc(doc(db, "barbearia", barbeariaId), { filaAberta: !filaAberta });
  };

  const adicionarManual = async () => {
    if (!manualNome || !manualServico) return alert("Preencha os campos obrigatórios");
    const servicoObj = servicos.find(s => s.id === manualServico);
    let bId = null; let bNome = null;
    if (manualBarbeiro) {
      const barb = barbeirosList.find(b => b.id === manualBarbeiro);
      if (barb) { bId = barb.id; bNome = barb.nome; }
    } else {
      const melhorBarbeiro = encontrarMelhorBarbeiro();
      if (melhorBarbeiro) { bId = melhorBarbeiro.id; bNome = melhorBarbeiro.nome; }
    }
    try {
      await addDoc(collection(db, "barbearia", barbeariaId, "fila"), {
        clienteNome: manualNome, clienteEmail: manualEmail,
        servicoNome: servicoObj?.nome || "Serviço Manual",
        servicoValor: servicoObj?.valor || "0",
        duracaoEstimada: servicoObj?.tempo || 30,
        dataEntrada: Timestamp.now(), status: "aguardando", origem: "balcao",
        barbeariaId: barbeariaId, barbeiroId: bId, barbeiroNome: bNome
      });
      setModalManualOpen(false); setManualNome(""); setManualEmail(""); setManualServico(""); setManualBarbeiro("");
    } catch (error) { alert("Erro ao adicionar."); }
  };

  const encontrarMelhorBarbeiro = () => {
    if (barbeirosList.length === 0) return null;
    const stats = barbeirosList.map(b => ({ id: b.id, nome: b.nome, tempoTotal: 0, ultimaEntrada: 0 }));
    [...fila, ...emAtendimento].forEach(t => {
      const idx = stats.findIndex(b => b.id === t.barbeiroId);
      if (idx !== -1) {
        stats[idx].tempoTotal += (Number(t.duracaoEstimada) || 30);
        const time = t.dataEntrada?.seconds || 0;
        if (time > stats[idx].ultimaEntrada) stats[idx].ultimaEntrada = time;
      }
    });
    stats.sort((a, b) => { if (a.tempoTotal !== b.tempoTotal) return a.tempoTotal - b.tempoTotal; return a.ultimaEntrada - b.ultimaEntrada; });
    return stats[0];
  };

  const getNextAppointment = () => {
    const now = new Date();
    return agendamentosHoje.find(ag => parseTime(ag.horaInicio) > now);
  };
  const nextApp = getNextAppointment();

  const verificarConflito = (duracaoServicoMinutos) => {
    if (!nextApp) return null;
    if (!isOwner && nextApp.barbeiroId !== userId) return null;

    const now = new Date(); const tempoServicoMs = duracaoServicoMinutos * 60 * 1000;
    const horaTerminoPrevista = new Date(now.getTime() + tempoServicoMs);
    const horaInicioAgendamento = parseTime(nextApp.horaInicio);

    if (horaTerminoPrevista > horaInicioAgendamento) {
      return { conflito: true, agendamento: nextApp, atraso: Math.round((horaTerminoPrevista - horaInicioAgendamento) / 60000) };
    }
    return null;
  };

  const openConfirm = (title, message, action, type = "info") => setConfirmModal({ open: true, title, message, onConfirm: action, type });
  const closeConfirm = () => setConfirmModal({ ...confirmModal, open: false });
  const executeConfirm = () => { if (confirmModal.onConfirm) confirmModal.onConfirm(); closeConfirm(); };

  const handleClickChamar = (ticket) => {
    const resultadoConflito = verificarConflito(ticket.duracaoEstimada || 30);
    if (resultadoConflito) {
      openConfirm("Choque de Horário!", `Vai atrasar ${resultadoConflito.agendamento.clienteNome} (${resultadoConflito.agendamento.horaInicio}) em ${resultadoConflito.atraso} min.`, () => processarChamada(ticket), "warning");
    } else { processarChamada(ticket); }
  };

  const processarChamada = async (ticket) => {
    const updates = { status: "chamado", horaChamada: Timestamp.now(), chamadoPor: userId };
    if (!ticket.barbeiroId) { updates.barbeiroId = userId; updates.barbeiroNome = userName; }
    await updateDoc(doc(db, "barbearia", barbeariaId, "fila", ticket.id), updates);
  };

  const interromperChamado = (ticket) => {
    openConfirm("Interromper", `Parar de chamar ${ticket.clienteNome}?`, async () => {
      await updateDoc(doc(db, "barbearia", barbeariaId, "fila", ticket.id), { status: "aguardando", horaChamada: null, chamadoPor: null });
    }, "info");
  };

  const iniciarCorte = async (ticket) => {
    const updates = { status: "em_atendimento", atendidoPor: userId, inicioAtendimento: serverTimestamp() };
    if (!ticket.barbeiroId) { updates.barbeiroId = userId; updates.barbeiroNome = userName; }
    await updateDoc(doc(db, "barbearia", barbeariaId, "fila", ticket.id), updates);
  };

  const finalizarCorte = (ticket) => {
    openConfirm("Finalizar", `Concluir serviço de ${ticket.clienteNome}?`, async () => {
      await updateDoc(doc(db, "barbearia", barbeariaId, "fila", ticket.id), { status: "finalizado", fimAtendimento: serverTimestamp() });
    }, "success");
  };

  const cancelarFila = (ticket) => {
    openConfirm("Remover", `Remover ${ticket.clienteNome} da fila?`, async () => {
      await updateDoc(doc(db, "barbearia", barbeariaId, "fila", ticket.id), { status: "cancelado" });
    }, "danger");
  };

  const formatHora = (timestamp) => { if (!timestamp) return "--:--"; return timestamp.toDate().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); };

  const filaFiltrada = fila.filter(t => {
    if (filterBarbeiro) return t.barbeiroId === filterBarbeiro;
    if (isOwner) return true;
    return (!t.barbeiroId || t.barbeiroId === userId);
  });

  const servicosDisponiveisManual = manualBarbeiro
    ? servicos.filter(s => {
      const barb = barbeirosList.find(b => b.id === manualBarbeiro);
      return barb && Array.isArray(barb.servicosIds) && barb.servicosIds.includes(s.id);
    })
    : servicos;

  if (loadingId) return <LoadingComLayout />;

  return (
    <>
      <Main>
        {/* TOASTS CONTAINER */}
        <ToastContainer>
          {notifications.map(notif => (
            <Toast key={notif.id}>
              <div className="icon-box"><LuBellRing /></div>
              <div>
                <h4>{notif.title}</h4>
                <p>{notif.message}</p>
              </div>
              {/* BOTÃO FECHAR NOTIFICAÇÃO */}
              <ToastCloseBtn onClick={() => removeNotification(notif.id)}>
                <LuX size={16} />
              </ToastCloseBtn>
            </Toast>
          ))}
        </ToastContainer>

        <PageHeader>
          {/* Layout Spacer */}
        </PageHeader>

        {/* Banner do Próximo Agendamento */}
        {nextApp && (
          <NextAppointmentCard>
            <NextAppInfo>
              <LuCalendarClock size={28} />
              <div>
                Próximo Agendamento: {nextApp.horaInicio}
                <br />
                <span>{nextApp.clienteNome} - {nextApp.servicoNome} com {nextApp.barbeiroNome || 'Barbeiro não definido'}</span>
              </div>
            </NextAppInfo>
            <div style={{ textAlign: 'right', fontSize: '0.9rem' }}>(Prioridade)</div>
          </NextAppointmentCard>
        )}

        <GridArea>
          {/* CARD DA FILA */}
          <Card>
            <Toolbar>
              <ToolbarTop>
                <ToolbarTitle>
                  <LuUsers size={22} color="#3a79ed" />
                  Fila de Espera
                  <span className="count-badge">{filaFiltrada.length}</span>
                </ToolbarTitle>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* --- NOVO BOTÃO MODO TV --- */}
                  <ActionButton
                    bg="#1f1a40" // Cor escura combinando com o tema da TV
                    color="#fff"
                    onClick={abrirModoTv}
                    style={{ flex: 'initial', padding: '0.4rem 1rem' }}
                    title="Abrir Painel para TV"
                  >
                    <LuTv size={16} /> Modo TV
                  </ActionButton>

                  {/* Botão Existente de Abrir/Fechar Fila */}
                  <ActionButton
                    bg={filaAberta ? "#fee2e2" : "#dcfce7"}
                    color={filaAberta ? "#ef4444" : "#16a34a"}
                    onClick={toggleFilaStatus}
                    style={{ flex: 'initial', padding: '0.4rem 1rem' }}
                  >
                    {filaAberta ? <><LuLock size={16} /> Fechar Fila</> : <><LuLockOpen size={16} /> Abrir Fila</>}
                  </ActionButton>
                </div>
              </ToolbarTop>

              <ToolbarControls>
                {/* Configs e Filtros */}
                <ControlGroup>
                  <ToolbarLabel><LuHourglass size={16} /> Tolerância:</ToolbarLabel>
                  <ToolbarSelect
                    value={tolerancia}
                    onChange={(e) => handleToleranceChange(e.target.value)}
                    title="Tempo de Tolerância"
                  >
                    <option value={0.5}>30s</option>
                    <option value={1}>1 min</option>
                    <option value={3}>3 min</option>
                    <option value={5}>5 min</option>
                    <option value={10}>10 min</option>
                    <option value={15}>15 min</option>
                    <option value={20}>20 min</option>
                  </ToolbarSelect>
                </ControlGroup>

                {isOwner && (
                  <ControlGroup>
                    <ToolbarLabel><LuFilter size={14} /> Filtrar:</ToolbarLabel>
                    <ToolbarSelect
                      value={filterBarbeiro}
                      onChange={e => setFilterBarbeiro(e.target.value)}
                    >
                      <option value="">Todos os Barbeiros</option>
                      {barbeirosList.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                    </ToolbarSelect>
                  </ControlGroup>
                )}

                <div style={{ marginLeft: 'auto' }}>
                  <ActionButton
                    bg="#3a79ed" color="#fff"
                    onClick={() => setModalManualOpen(true)}
                    style={{ flex: 'initial', padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                  >
                    <LuPlus size={16} /> Novo Cliente
                  </ActionButton>
                </div>
              </ToolbarControls>
            </Toolbar>

            <CardBody>
              {!filaAberta && (
                <ClosedBanner>
                  <LuLock style={{ marginBottom: -2 }} /> FILA FECHADA
                </ClosedBanner>
              )}

              {filaFiltrada.length === 0 && <EmptyState><LuClock /><p>Nenhum cliente aguardando.</p></EmptyState>}

              {filaFiltrada.map((ticket, index) => {
                const isChamado = ticket.status === 'chamado';
                return (
                  <TicketCard key={ticket.id} bg={isChamado ? "#ecfdf5" : "#fff"} borderColor={isChamado ? "#34d399" : "#e5e7eb"}>
                    <TicketHeader>
                      <div>
                        <ClientName>{ticket.clienteNome}</ClientName>
                        <TicketMeta>{ticket.servicoNome} • {ticket.duracaoEstimada} min</TicketMeta>
                        {ticket.barbeiroNome && (
                          <StatusTag bg="#e0f2fe" color="#0284c7" style={{ marginTop: 5, display: 'inline-block' }}>
                            <LuUser size={10} style={{ marginRight: 3 }} /> {ticket.barbeiroNome}
                          </StatusTag>
                        )}
                        {ticket.origem === 'balcao' && <span style={{ marginLeft: 8, fontSize: 10, color: '#94a3b8' }}>Manual</span>}
                      </div>
                      <StatusTag bg={isChamado ? "#34d399" : "#eef2fb"} color={isChamado ? "#fff" : "#3a79ed"}>
                        {isChamado ? "CHAMANDO" : `#${index + 1}`}
                      </StatusTag>
                    </TicketHeader>

                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <LuClock size={12} /> Entrada: {formatHora(ticket.dataEntrada)}
                    </div>

                    {/* Timer Renderizado Aqui */}
                    {isChamado && ticket.horaChamada && (
                      <TicketTimer
                        startTime={ticket.horaChamada}
                        toleranceMinutes={tolerancia}
                      />
                    )}

                    <ActionRow>
                      {!isChamado ? (
                        <ActionButton bg="#3a79ed" color="#fff" onClick={() => handleClickChamar(ticket)}>
                          <LuMegaphone size={16} /> Chamar
                        </ActionButton>
                      ) : (
                        <>
                          <ActionButton bg="#10b981" color="#fff" onClick={() => iniciarCorte(ticket)}>
                            <LuScissors size={16} /> Iniciar
                          </ActionButton>
                          <ActionButton bg="#fbbf24" color="#fff" style={{ flex: 0, padding: '0 1rem' }} onClick={() => interromperChamado(ticket)}>
                            <LuUndo2 size={18} />
                          </ActionButton>
                        </>
                      )}
                      {(isOwner || ticket.barbeiroId === userId || !ticket.barbeiroId) && (
                        <ActionButton bg="#fee2e2" color="#ef4444" style={{ flex: 0, padding: '0 1rem' }} onClick={() => cancelarFila(ticket)}>
                          <LuX size={18} />
                        </ActionButton>
                      )}
                    </ActionRow>
                  </TicketCard>
                );
              })}
            </CardBody>
          </Card>

          {/* CARD NA CADEIRA */}
          <Card>
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #eef2fb', display: 'flex', alignItems: 'center', gap: 10 }}>
              <LuArmchair size={22} color="#3a79ed" />
              <span style={{ fontWeight: 700, color: '#1f1a40' }}>Em Atendimento ({emAtendimento.length})</span>
            </div>
            <CardBody>
              {emAtendimento.length === 0 && <EmptyState><LuScissors /><p>Ninguém sendo atendido.</p></EmptyState>}
              {emAtendimento.map((ticket) => (
                <TicketCard key={ticket.id} borderColor="#3a79ed" bg="#f8fafc">
                  <TicketHeader>
                    <div>
                      <ClientName>{ticket.clienteNome}</ClientName>
                      <TicketMeta>{ticket.servicoNome}</TicketMeta>
                      {ticket.barbeiroNome && (
                        <StatusTag bg="#e0f2fe" color="#0284c7" style={{ marginTop: 5, display: 'inline-block' }}>
                          {ticket.barbeiroNome}
                        </StatusTag>
                      )}
                    </div>
                  </TicketHeader>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 10 }}>
                    Início: {formatHora(ticket.inicioAtendimento)}
                  </div>
                  <ActionRow>
                    <ActionButton bg="#3a79ed" color="#fff" onClick={() => finalizarCorte(ticket)}>
                      <LuCheck /> Finalizar
                    </ActionButton>
                  </ActionRow>
                </TicketCard>
              ))}
            </CardBody>
          </Card>
        </GridArea>

        {/* --- MODAIS --- */}
        {/* Modal Confirm */}
        {confirmModal.open && (
          <ModalOverlay onClick={closeConfirm}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <h3>{confirmModal.title}</h3>
              <p>{confirmModal.message}</p>
              <ModalActions>
                <ActionButton bg="#e5e7eb" color="#374151" onClick={closeConfirm}>Cancelar</ActionButton>
                <ActionButton
                  bg={confirmModal.type === 'danger' ? '#ef4444' : confirmModal.type === 'warning' ? '#f59e0b' : '#3a79ed'}
                  color="white"
                  onClick={executeConfirm}
                >
                  Confirmar
                </ActionButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Modal Add Manual */}
        {modalManualOpen && (
          <ModalOverlay onClick={() => setModalManualOpen(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, marginBottom: 20 }}>Novo Cliente (Manual)</h3>
              <InputGroup>
                <label>Nome do Cliente</label>
                <input value={manualNome} onChange={e => setManualNome(e.target.value)} placeholder="Ex: João Silva" />
              </InputGroup>
              <InputGroup>
                <label>Preferência de Barbeiro</label>
                <select value={manualBarbeiro} onChange={e => { setManualBarbeiro(e.target.value); setManualServico(""); }}>
                  <option value="">🔄 Automático / Fila Geral</option>
                  {barbeirosList.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                </select>
              </InputGroup>
              <InputGroup>
                <label>Serviço</label>
                <select value={manualServico} onChange={e => setManualServico(e.target.value)}>
                  <option value="">Selecione...</option>
                  {servicosDisponiveisManual.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.tempo} min)</option>)}
                </select>
                {manualBarbeiro && servicosDisponiveisManual.length === 0 && <small style={{ color: 'red' }}>Sem serviços cadastrados para este barbeiro.</small>}
              </InputGroup>
              <ModalActions>
                <ActionButton bg="#e5e7eb" color="#374151" onClick={() => setModalManualOpen(false)}>Cancelar</ActionButton>
                <ActionButton bg="#3a79ed" color="white" onClick={adicionarManual}><LuPlus /> Adicionar</ActionButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </Main>
    </>
  );
}